import { useState } from "react";
import { Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { whatsappLink } from "@/config/site";
import { track } from "@/lib/analytics";

const PREMISES = ["Office", "Retail shop", "Café or restaurant", "Salon or barbershop", "Gym", "Clinic", "Apartment common area", "Warehouse", "Other"];
const FREQUENCIES = ["One-off", "Daily", "Weekly", "Fortnightly", "Monthly", "Customised schedule"];
const SERVICES_LIST = ["Office cleaning", "Retail cleaning", "Restaurant or café cleaning", "Salon or barbershop cleaning", "Gym cleaning", "Common-area cleaning", "Deep cleaning", "Other"];

const inputCls = "h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20 focus-visible:border-emerald-600";

const Field = ({ label, required, children, testid }) => (
  <div className="flex flex-col gap-1.5" data-testid={testid}>
    <Label className="text-sm font-semibold text-slate-700">{label} {required && <span className="text-emerald-600">*</span>}</Label>
    {children}
  </div>
);

export const BusinessContractForm = () => {
  const [form, setForm] = useState({
    business_name: "", contact_person: "", phone: "", email: "", business_address: "",
    premises_type: "", approx_size: "", num_toilets: "", frequency: "", preferred_days: "",
    preferred_time: "", current_condition: "", start_date: "", message: "", company: "",
  });
  const [services, setServices] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (s) => setServices((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const validate = () => {
    const e = {};
    if (!form.business_name.trim()) e.business_name = "Required";
    if (!form.contact_person.trim()) e.contact_person = "Required";
    if (!/^[+\d][\d\s()-]{5,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!consent) e.consent = "Please tick the consent box";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      await api.post("/contracts", { ...form, services, consent, source_page: "/business-cleaning-contracts-dublin" });
      track.formSubmit("business-contract");
      setDone(true);
      window.scrollTo({ top: document.getElementById("contract-form")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try WhatsApp.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div id="contract-form" className="rounded-3xl border border-emerald-200 bg-white p-10 text-center shadow-xl" data-testid="contract-success">
        <CheckCircle2 size={52} className="mx-auto text-emerald-600" />
        <h3 className="mt-4 font-display text-2xl font-black text-slate-900">Thank you</h3>
        <p className="mt-2 text-slate-600">We've received your business cleaning enquiry and will be in touch to arrange a site visit or quote. For a faster response, message us on WhatsApp.</p>
        <a href={whatsappLink("Hello, I would like a quote for a business cleaning contract.")} onClick={() => track.whatsappClick("contract-success")} target="_blank" rel="noreferrer"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-bold text-white transition-transform hover:scale-105">
          <MessageCircle size={18} /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form id="contract-form" onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-9" data-testid="contract-form" noValidate>
      <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Business name" required testid="cf-business"><Input data-testid="cf-business-name" className={inputCls} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />{errors.business_name && <span className="text-xs text-red-500">{errors.business_name}</span>}</Field>
        <Field label="Contact person" required testid="cf-contact"><Input data-testid="cf-contact-person" className={inputCls} value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />{errors.contact_person && <span className="text-xs text-red-500">{errors.contact_person}</span>}</Field>
        <Field label="Phone" required testid="f-cf-phone"><Input data-testid="cf-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />{errors.phone && <span className="text-xs text-red-500">{errors.phone}</span>}</Field>
        <Field label="Email" required testid="f-cf-email"><Input data-testid="cf-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />{errors.email && <span className="text-xs text-red-500">{errors.email}</span>}</Field>
        <Field label="Business address or Eircode" testid="f-cf-address"><Input data-testid="cf-address" className={inputCls} value={form.business_address} onChange={(e) => set("business_address", e.target.value)} /></Field>
        <Field label="Type of premises" testid="cf-premises">
          <Select value={form.premises_type} onValueChange={(v) => set("premises_type", v)}>
            <SelectTrigger data-testid="cf-premises-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{PREMISES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Approximate size (sq m / desks)" testid="f-cf-size"><Input data-testid="cf-size" className={inputCls} value={form.approx_size} onChange={(e) => set("approx_size", e.target.value)} placeholder="e.g. 120 sq m" /></Field>
        <Field label="Number of toilets" testid="f-cf-toilets"><Input data-testid="cf-toilets" className={inputCls} value={form.num_toilets} onChange={(e) => set("num_toilets", e.target.value)} inputMode="numeric" /></Field>
        <Field label="Preferred cleaning frequency" testid="cf-frequency">
          <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
            <SelectTrigger data-testid="cf-frequency-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{FREQUENCIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Preferred cleaning days" testid="f-cf-days"><Input data-testid="cf-days" className={inputCls} value={form.preferred_days} onChange={(e) => set("preferred_days", e.target.value)} placeholder="e.g. Mon, Wed, Fri" /></Field>
        <Field label="Preferred time" testid="f-cf-time"><Input data-testid="cf-time" className={inputCls} value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} placeholder="e.g. after 6pm" /></Field>
        <Field label="Current condition" testid="f-cf-condition"><Input data-testid="cf-condition" className={inputCls} value={form.current_condition} onChange={(e) => set("current_condition", e.target.value)} /></Field>
        <Field label="Contract start date" testid="cf-start"><Input data-testid="cf-start-date" type="date" className={inputCls} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
      </div>

      <div className="mt-6">
        <Label className="text-sm font-semibold text-slate-700">Services required</Label>
        <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {SERVICES_LIST.map((s) => (
            <label key={s} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${services.includes(s) ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
              <Checkbox checked={services.includes(s)} onCheckedChange={() => toggle(s)} className="data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" /> {s}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <Field label="Additional information" testid="f-cf-message"><Textarea data-testid="cf-message" className="min-h-24 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20" value={form.message} onChange={(e) => set("message", e.target.value)} /></Field>
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm text-slate-600">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="cf-consent" className="mt-0.5 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" />
        <span>I agree to be contacted regarding my business cleaning enquiry.</span>
      </label>
      {errors.consent && <span className="mt-1 block text-xs text-red-500">{errors.consent}</span>}

      <button type="submit" disabled={submitting} data-testid="cf-submit" className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-700 px-8 py-4 text-base font-bold text-white transition-all hover:bg-emerald-800 disabled:opacity-60">
        {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Send Business Enquiry"}
      </button>
    </form>
  );
};
